/**
 * LEMUR Copper — Shopify Theme JS
 * Vanilla ES6+, no dependencies
 * Web Components for interactive UI
 */

/* ── Helpers ── */
const on = (el, ev, fn, opts) => el.addEventListener(ev, fn, opts);
const off = (el, ev, fn) => el.removeEventListener(ev, fn);
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const trapFocus = (el) => {
  const focusable = $$('a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])', el);
  if (!focusable.length) return;
  const first = focusable[0], last = focusable[focusable.length - 1];
  const handler = (e) => {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) { if (document.activeElement === first) { e.preventDefault(); last.focus(); } }
    else { if (document.activeElement === last) { e.preventDefault(); first.focus(); } }
  };
  on(el, 'keydown', handler);
  return handler;
};
const releaseFocus = (el, handler) => { if (handler) off(el, 'keydown', handler); };

/* ── Site Header scroll behaviour ── */
(function () {
  const header = $('.site-header');
  if (!header) return;
  const transparent = header.classList.contains('site-header--transparent');
  let ticking = false;
  const update = () => {
    if (window.scrollY > 10) header.classList.add('scrolled');
    else if (transparent) header.classList.remove('scrolled');
    ticking = false;
  };
  on(window, 'scroll', () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
})();

/* ── Mobile Menu ── */
(function () {
  const btn = $('.js-menu-toggle');
  const menu = $('.mobile-menu');
  const closeBtn = $('.js-menu-close');
  if (!btn || !menu) return;
  let trap;
  const open = () => {
    menu.classList.add('open');
    btn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    trap = trapFocus(menu);
    menu.querySelector('a, button')?.focus();
  };
  const close = () => {
    menu.classList.remove('open');
    btn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    releaseFocus(menu, trap);
    btn.focus();
  };
  on(btn, 'click', () => menu.classList.contains('open') ? close() : open());
  if (closeBtn) on(closeBtn, 'click', close);
  on(document, 'keydown', (e) => { if (e.key === 'Escape' && menu.classList.contains('open')) close(); });
})();

/* ── Search Modal ── */
class SearchModal extends HTMLElement {
  connectedCallback() {
    this.modal = $('.search-modal', this);
    this.input = $('.search-modal__input', this);
    const closeBtns = $$('.js-search-close');
    const openBtns = $$('.js-search-open');
    openBtns.forEach(b => on(b, 'click', () => this.open()));
    closeBtns.forEach(b => on(b, 'click', () => this.close()));
    on(document, 'keydown', (e) => { if (e.key === 'Escape') this.close(); });
  }
  open() {
    this.modal.classList.add('open');
    document.body.style.overflow = 'hidden';
    this.input?.focus();
  }
  close() {
    this.modal.classList.remove('open');
    document.body.style.overflow = '';
  }
}
customElements.define('search-modal', SearchModal);

/* ── Cart Drawer ── */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.drawer = $('.cart-drawer', this);
    this.overlay = $('.cart-drawer__overlay', this);
    $$('.js-cart-open').forEach(b => on(b, 'click', () => this.open()));
    $$('.js-cart-close').forEach(b => on(b, 'click', () => this.close()));
    if (this.overlay) on(this.overlay, 'click', () => this.close());
    on(document, 'keydown', (e) => { if (e.key === 'Escape') this.close(); });
    on(document, 'cart:open', () => this.open());
    on(document, 'cart:refresh', () => this.refresh());
  }
  open() {
    this.drawer.classList.add('open');
    this.overlay?.classList.add('visible');
    document.body.style.overflow = 'hidden';
    this.refresh();
  }
  close() {
    this.drawer.classList.remove('open');
    this.overlay?.classList.remove('visible');
    document.body.style.overflow = '';
  }
  refresh() {
    // Show loading state
    const itemsEl = this.querySelector('#cart-drawer-items');
    if (itemsEl) itemsEl.classList.add('cart-drawer__items--loading');

    // Disable checkout button during refresh
    const checkoutBtn = this.querySelector('.cart-drawer__checkout');
    if (checkoutBtn) {
      checkoutBtn.setAttribute('aria-disabled', 'true');
      checkoutBtn.style.pointerEvents = 'none';
      checkoutBtn.style.opacity = '0.4';
    }

    fetch('/cart?view=drawer', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(r => r.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Replace cart items (new element already has loading overlay in markup)
        const newItems = doc.getElementById('cart-drawer-items');
        const currentItems = this.querySelector('#cart-drawer-items');
        if (newItems && currentItems) currentItems.replaceWith(newItems);

        // Replace footer (subtotal, shipping bar, checkout button)
        const newFooter = doc.querySelector('.cart-drawer__footer');
        const currentFooter = this.querySelector('.cart-drawer__footer');
        if (newFooter) {
          if (currentFooter) currentFooter.replaceWith(newFooter);
          else this.querySelector('.cart-drawer')?.append(newFooter);
        } else if (currentFooter) {
          currentFooter.remove();
        }

        // Update header item count
        const newHeader = doc.querySelector('.cart-drawer__title');
        const currentHeader = this.querySelector('.cart-drawer__title');
        if (newHeader && currentHeader) currentHeader.innerHTML = newHeader.innerHTML;

        // Sync header cart count badge from the already-fetched drawer HTML
        const itemCount = parseInt(newHeader?.dataset?.itemCount ?? '0', 10);
        $$('.site-header__cart-count').forEach(el => {
          el.textContent = itemCount;
          el.style.display = itemCount > 0 ? 'flex' : 'none';
        });
      })
      .catch(() => {
        // On error, remove loading state so drawer isn't stuck
        const items = this.querySelector('#cart-drawer-items');
        if (items) items.classList.remove('cart-drawer__items--loading');
        const btn = this.querySelector('.cart-drawer__checkout');
        if (btn) {
          btn.removeAttribute('aria-disabled');
          btn.style.pointerEvents = '';
          btn.style.opacity = '';
        }
      });
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ── Product Form ── */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = $('form', this);
    if (!this.form) return;
    on(this.form, 'submit', this.onSubmit.bind(this));
  }
  onSubmit(e) {
    e.preventDefault();
    const btn = $('[type="submit"]', this.form);
    if (!btn) return;
    btn.classList.add('btn--loading');
    btn.setAttribute('aria-disabled', 'true');
    const data = new FormData(this.form);
    fetch('/cart/add.js', { method: 'POST', body: data, headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(r => r.json())
      .then(() => {
        document.dispatchEvent(new CustomEvent('cart:open'));
        this.updateCartCount();
      })
      .catch(() => {})
      .finally(() => {
        btn.classList.remove('btn--loading');
        btn.removeAttribute('aria-disabled');
      });
  }
  updateCartCount() {
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(r => r.json())
      .then(cart => {
        $$('.site-header__cart-count').forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        });
      });
  }
}
customElements.define('product-form', ProductForm);

/* ── Variant Selects ── */
class VariantSelects extends HTMLElement {
  connectedCallback() {
    on(this, 'change', this.onVariantChange.bind(this));
  }
  onVariantChange(e) {
    const variant = this.getSelectedVariant();
    if (!variant) return;
    this.updatePrice(variant);
    this.updateAvailability(variant);
    this.updateHistoryUrl(variant);
    this.updateMedia(variant);
    this.dispatchEvent(new CustomEvent('variant:change', { detail: { variant }, bubbles: true }));
  }
  getSelectedVariant() {
    const productData = JSON.parse(this.dataset.productJson || '{}');
    if (!productData.variants) return null;
    const selected = {};
    $$('[data-option-index]', this).forEach(el => {
      const idx = el.dataset.optionIndex;
      const active = $('.variant-btn.active', el) || $('[data-value].active', el);
      if (active) selected[idx] = active.dataset.value || active.textContent.trim();
    });
    return productData.variants.find(v =>
      v.options.every((opt, i) => selected[i] === opt)
    );
  }
  updatePrice(variant) {
    const priceEl = $('.product-info__price');
    if (!priceEl || !variant) return;
    const formatter = new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency: window.Shopify?.currency?.active || 'CHF' });
    priceEl.textContent = formatter.format(variant.price / 100);
  }
  updateAvailability(variant) {
    const btn = $('[data-add-to-cart]');
    if (!btn || !variant) return;
    if (variant.available) {
      btn.removeAttribute('disabled');
      btn.textContent = btn.dataset.addToCart;
    } else {
      btn.setAttribute('disabled', 'disabled');
      btn.textContent = btn.dataset.soldOut;
    }
  }
  updateHistoryUrl(variant) {
    window.history.replaceState({}, '', `?variant=${variant.id}`);
  }
  updateMedia(variant) {
    if (!variant?.featured_media?.id) return;
    const thumb = $(`.product-gallery__thumb[data-media-id="${variant.featured_media.id}"]`);
    if (thumb) thumb.click();
  }
}
customElements.define('variant-selects', VariantSelects);

/* ── Variant button clicks ── */
on(document, 'click', (e) => {
  const btn = e.target.closest('.variant-btn');
  if (!btn) return;
  const group = btn.closest('[data-option-index]');
  if (!group) return;
  $$('.variant-btn', group).forEach(b => b.classList.remove('active'));
  btn.classList.add('active');
  btn.closest('variant-selects')?.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ── Product Gallery ── */
(function () {
  on(document, 'click', (e) => {
    const thumb = e.target.closest('.product-gallery__thumb');
    if (!thumb) return;
    const gallery = thumb.closest('.product-gallery');
    if (!gallery) return;
    const main = $('.product-gallery__main img', gallery);
    const mainMedia = $('.product-gallery__main', gallery);
    if (!main) return;
    $$('.product-gallery__thumb', gallery).forEach(t => t.classList.remove('active'));
    thumb.classList.add('active');
    const src = thumb.dataset.src;
    const srcset = thumb.dataset.srcset;
    if (src) main.src = src;
    if (srcset) main.srcset = srcset;
    main.alt = thumb.dataset.alt || '';
  });
})();

/* ── Quantity Input ── */
on(document, 'click', (e) => {
  const btn = e.target.closest('[data-qty-change]');
  if (!btn) return;
  const input = btn.closest('.qty-input, .cart-qty')?.querySelector('input');
  if (!input) return;
  const change = parseInt(btn.dataset.qtyChange, 10);
  const newVal = Math.max(1, parseInt(input.value, 10) + change);
  input.value = newVal;
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ── Cart line item update ── */
on(document, 'change', (e) => {
  const input = e.target.closest('.cart-qty__input');
  if (!input) return;
  const lineKey = input.dataset.key;
  if (!lineKey) return;
  fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity: parseInt(input.value, 10) })
  }).then(() => document.dispatchEvent(new CustomEvent('cart:refresh')));
});

/* ── Cart remove ── */
on(document, 'click', (e) => {
  const btn = e.target.closest('[data-cart-remove]');
  if (!btn) return;
  const key = btn.dataset.cartRemove;
  fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: 0 })
  }).then(() => document.dispatchEvent(new CustomEvent('cart:refresh')));
});

/* ── Accordion ── */
on(document, 'click', (e) => {
  const btn = e.target.closest('.accordion__btn');
  if (!btn) return;
  e.preventDefault();
  const item = btn.closest('.accordion__item');
  const isOpen = item.hasAttribute('open');
  // Close others in same container (optional single-open behavior)
  const container = item.closest('.accordion--single');
  if (container) $$('.accordion__item[open]', container).forEach(i => i.removeAttribute('open'));
  if (!isOpen) item.setAttribute('open', '');
  else item.removeAttribute('open');
});

/* ── Newsletter form ── */
on(document, 'submit', (e) => {
  const form = e.target.closest('.newsletter-form');
  if (!form) return;
  e.preventDefault();
  const input = $('input[type="email"]', form);
  const msg = $('.newsletter-form__message', form);
  if (!input?.value) return;
  const btn = $('button', form);
  btn.classList.add('btn--loading');
  fetch(form.action, { method: 'POST', body: new FormData(form) })
    .then(() => {
      if (msg) { msg.textContent = form.dataset.successMsg || 'Thanks for subscribing!'; msg.className = 'newsletter-form__message newsletter-form__message--success'; }
      input.value = '';
    })
    .catch(() => {
      if (msg) { msg.textContent = form.dataset.errorMsg || 'An error occurred.'; msg.className = 'newsletter-form__message newsletter-form__message--error'; }
    })
    .finally(() => btn.classList.remove('btn--loading'));
});

/* ── Video section ── */
on(document, 'click', (e) => {
  const playBtn = e.target.closest('.video-play-btn');
  if (!playBtn) return;
  const wrapper = playBtn.closest('.video-wrapper');
  const iframe = $('iframe', wrapper);
  const video = $('video', wrapper);
  if (iframe) {
    const src = iframe.src;
    iframe.src = src.includes('?') ? src + '&autoplay=1' : src + '?autoplay=1';
  } else if (video) {
    video.play();
  }
  playBtn.style.display = 'none';
});

/* ── Predictive search ── */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = $('input', this);
    this.results = $('.predictive-search__results', this);
    if (!this.input) return;
    this.debounceTimer = null;
    on(this.input, 'input', () => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.search(this.input.value), 300);
    });
  }
  search(q) {
    if (q.length < 2) { if (this.results) this.results.innerHTML = ''; return; }
    fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5`)
      .then(r => r.json())
      .then(data => this.renderResults(data?.resources?.results?.products || []))
      .catch(() => {});
  }
  renderResults(products) {
    if (!this.results) return;
    if (!products.length) { this.results.innerHTML = ''; return; }
    this.results.innerHTML = `<div class="search-results__grid">${products.map(p => `
      <a href="${p.url}" class="product-card">
        <div class="product-card__media media media--square">
          ${p.featured_image?.url ? `<img src="${p.featured_image.url}&width=300" alt="${p.title}" loading="lazy">` : ''}
        </div>
        <div class="product-card__info">
          <p class="product-card__title">${p.title}</p>
        </div>
      </a>`).join('')}</div>`;
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* ── Reveal on scroll (fallback for inline script) ── */
if ('IntersectionObserver' in window) {
  const obs = new IntersectionObserver((entries) => {
    entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('revealed'); obs.unobserve(e.target); } });
  }, { threshold: 0.08 });
  document.querySelectorAll('.reveal').forEach(el => obs.observe(el));
}
